import { useEditorEvent, useSuggest } from '@remirror/react-hooks';
import IconData from './../../../../cms/src/components/IconsData';
import React, { useEffect, useState, useCallback } from 'react';
import { SEARCH_API_URL, USER_SUGGESTIONS } from 'cms/src/utils/apiEndpoints';
import { useGetAllShortcutOptions } from '../../hooks';
import { useActive, useChainedCommands, useRemirrorContext } from '@remirror/react';
import RemirrorShortcut from './RemirrorShortcut';
import EmojiShortcut from './EmojiShortcut';
import clientService from 'cms/src/apiClient';
import MentionShortcut from './MentionShortcut';
import DatePickerShortcut from './DatePickerShortcut';
import {
  CLOSE_SHORTCUT_MENU_EVENT,
  OPEN_SHORTCUT_MENU_EVENT,
  OPEN_SHORTCUT_MENU_EVENT_WITH_CONTEXT,
} from './DragAndDropExtention/plugins/DragHandlePlugin';
import { ICONS_MAP } from '../../icons';

const Enter = 'Enter';
const ArrowUp = 'ArrowUp';
const ArrowDown = 'ArrowDown';
const ArrowLeft = 'ArrowLeft';
const ArrowRight = 'ArrowRight';

const transformIconData = (iconData) => {
  return Object.entries(iconData).map(([code, emoji]) => ({
    code,
    emoji,
    name: code
      .split('-')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' '),
  }));
};

const fetchSuggestions = async (query) => {
  try {
    const searchParams = new URLSearchParams({ q: query });
    const searchApiParams = new URLSearchParams({ q: query, size: 5, from: 0 });

    const userSuggestionsPromise = clientService.get(`${USER_SUGGESTIONS}?${searchParams}`);
    const searchApiPromise = clientService.get(`${SEARCH_API_URL}?${searchApiParams}`);

    const [userRes, searchRes] = await Promise.all([userSuggestionsPromise, searchApiPromise]);

    return {
      users: userRes.data,
      searchResults: searchRes.data,
    };
  } catch (error) {
    return {
      users: [],
      searchResults: {
        documents: [],
        workspaces: [],
      },
    };
  }
};

export const ShortcutHOC = ({ editable, fileId, workspaceId, organizationId, disableRichFeatures = false }) => {
  const { features, filterShortcutOptions } = useGetAllShortcutOptions();
  const active = useActive();
  const chained = useChainedCommands();
  const { view } = useRemirrorContext();
  const suggestion = useSuggest({ char: '/', name: 'slash' });
  const emojiSuggestion = useSuggest({ char: ':', name: 'emoji' });
  const mentionSuggestion = useSuggest({ char: '@', name: 'mention' });

  const { change: suggestionChange } = suggestion;
  const { change: emojiChange } = emojiSuggestion;
  const { change: mentionChange } = mentionSuggestion;

  const [slashState, setSlashState] = useState({
    open: false,
    features: features,
    selected: getFirstFeature(features),
    range: { from: 0, to: 0 },
  });
  const [emojiState, setEmojiState] = useState({
    open: false,
    items: transformIconData(IconData),
    selected: 0,
    range: { from: 0, to: 0 },
  });

  const [mentionState, setMentionState] = useState({
    open: false,
    items: { mentions: [], documents: [], workspaces: [] },
    selected: '',
    range: { from: 0, to: 0 },
  });
  const [showDatePicker, setShowDatePicker] = useState({ flag: false, from: 0 });

  const [openDialog, setOpenDialog] = useState(false);
  const [isContextMenu, setIsContextMenu] = useState(false);

  useEffect(() => {
    if (suggestionChange === undefined || suggestionChange.reason === 'start') {
      const range =
        suggestionChange === undefined
          ? { from: 0, to: 0 }
          : { from: suggestionChange.range.from, to: suggestionChange.range.to };

      if (suggestionChange?.reason === 'start') {
        setIsContextMenu(false);
      }

      setSlashState({
        open: suggestionChange?.reason === 'start',
        features: features,
        selected: getFirstFeature(features),
        range,
      });
    }
    if (suggestionChange !== undefined && suggestionChange?.reason === 'change-character') {
      const { query } = suggestionChange;
      setSlashState((prev) => {
        const filteredFeatures = filterShortcutOptions(query.full);
        return {
          ...prev,
          features: filteredFeatures,
          selected: getFirstFeature(filteredFeatures),
          range: { ...prev.range, to: suggestionChange.range.to },
        };
      });
    }
  }, [suggestionChange]);

  useEffect(() => {
    if (emojiChange === undefined || emojiChange.reason === 'start') {
      const range =
        emojiChange === undefined ? { from: 0, to: 0 } : { from: emojiChange.range.from, to: emojiChange.range.to };
      setEmojiState({
        open: emojiChange?.reason === 'start',
        items: transformIconData(IconData),
        selected: 0,
        range,
      });
    }
    if (emojiChange !== undefined && emojiChange?.reason === 'change-character') {
      const { query } = emojiChange;
      const items = transformIconData(IconData)?.filter(
        (icon) => icon.name.toLowerCase().includes(query.full) || icon.code.toLowerCase().includes(query.full)
      );
      setEmojiState((prev) => ({ ...prev, items, selected: 0 }));
    }
  }, [emojiChange]);

  useEffect(() => {
    if (mentionChange === undefined || mentionChange.reason === 'start') {
      const range =
        mentionChange === undefined
          ? { from: 0, to: 0 }
          : { from: mentionChange.range.from, to: mentionChange.range.to };
      setMentionState({
        open: mentionChange?.reason === 'start',
        items: { mentions: [], documents: [], workspaces: [] },
        selected: '',
        range,
      });
    }
    if (mentionChange !== undefined && mentionChange?.reason === 'change-character') {
      const { query } = mentionChange;
      if (query.full) {
        fetchSuggestions(query.full).then(({ users, searchResults }) => {
          setMentionState((prev) => {
            return {
              ...prev,
              items: {
                mentions: users.map((user) => ({
                  key: user.email,
                  displayName: user.name || user?.email,
                  user_info: user,
                  avatar: true,
                })),
                documents: searchResults.documents.map((document) => ({
                  key: document.id,
                  displayName: document.title,
                  document_info: {
                    content: document.content,
                    url: document.url,
                  },
                  icon: ICONS_MAP.document_outlined,
                })),
                workspaces: searchResults.workspaces.map((workspace) => ({
                  key: workspace.id,
                  displayName: workspace.name,
                  workspace_info: {
                    type: workspace.type,
                    url: workspace.url,
                  },
                  icon: ICONS_MAP.workspace_outlined,
                })),
              },
              selected: users[0]?.email ?? searchResults.documents[0]?.id ?? searchResults.workspaces[0]?.id ?? '',
            };
          });
        });
      } else {
        setMentionState((prev) => {
          return { ...prev, items: { mentions: [], documents: [], workspaces: [] }, selected: '' };
        });
      }
    }
  }, [mentionChange]);

  useEffect(() => {
    const handleShortcutMenuEventWithContext = (event) => {
      const { position } = event.detail;

      setIsContextMenu(true);

      const featuresWithContextMenu = filterShortcutOptions('', true);
      setSlashState({
        open: true,
        features: featuresWithContextMenu,
        selected: getFirstFeature(featuresWithContextMenu),
        range: { from: position.from, to: position.to },
      });
    };

    document.addEventListener(OPEN_SHORTCUT_MENU_EVENT_WITH_CONTEXT, handleShortcutMenuEventWithContext);

    return () => {
      document.removeEventListener(OPEN_SHORTCUT_MENU_EVENT_WITH_CONTEXT, handleShortcutMenuEventWithContext);
    };
  }, []);

  useEffect(() => {
    const handleShortcutMenu = (event) => {
      const { position } = event.detail;
      setSlashState({
        open: true,
        features: features,
        selected: getFirstFeature(filterShortcutOptions('')),
        range: { from: position, to: position },
      });
    };

    document.addEventListener(OPEN_SHORTCUT_MENU_EVENT, handleShortcutMenu);

    return () => {
      document.removeEventListener(OPEN_SHORTCUT_MENU_EVENT, handleShortcutMenu);
    };
  }, []);

  useEffect(() => {
    const handleShortcutCloseMenuEvent = (event) => {
      setSlashState({
        open: false,
        features: features,
        selected: getFirstFeature(features),
        range: { from: 0, to: 0 },
      });
    };

    document.addEventListener(CLOSE_SHORTCUT_MENU_EVENT, handleShortcutCloseMenuEvent);

    return () => {
      document.removeEventListener(CLOSE_SHORTCUT_MENU_EVENT, handleShortcutCloseMenuEvent);
    };
  }, []);

  const editorWrapper = document.querySelector('.remirror-theme');
  const width = editorWrapper?.offsetWidth;

  useEditorEvent('keydown', (event) => {
    if (!slashState.open && !emojiChange && !mentionChange) return false;

    if (emojiState.open) {
      const itemsPerRow = 7;
      const currentRow = Math.floor(emojiState.selected / itemsPerRow);
      const currentCol = emojiState.selected % itemsPerRow;
      let newSelection = emojiState.selected;
      if (event.code === ArrowDown || event.code === ArrowUp || event.code === ArrowLeft || event.code === ArrowRight) {
        switch (event.code) {
          case ArrowDown:
            if ((currentRow + 1) * itemsPerRow < emojiState.items.length) {
              newSelection = emojiState.selected + itemsPerRow;
              if (newSelection >= emojiState.items.length) {
                newSelection = emojiState.items.length - 1;
              }
            }
            break;
          case ArrowUp:
            if (currentRow > 0) {
              newSelection = emojiState.selected - itemsPerRow;
            }
            break;
          case ArrowLeft:
            if (currentCol > 0) {
              newSelection = emojiState.selected - 1;
            }
            break;
          case ArrowRight:
            if (currentCol < itemsPerRow - 1 && emojiState.selected < emojiState.items.length - 1) {
              newSelection = emojiState.selected + 1;
            }
            break;
        }
        setEmojiState((prev) => ({ ...prev, selected: newSelection }));
        return true;
      }

      if (event.code === Enter) {
        handleSelectedEmojiFeature(emojiState.items[emojiState.selected]);
        setEmojiState({ open: false, items: transformIconData(IconData), selected: 0, range: { from: 0, to: 0 } });
        return true;
      }
      return false;
    }
    if (slashState.open) {
      if (event.code === ArrowUp) {
        setSlashState((prev) => {
          const prevFeature = getPreviousFeature(slashState.features, slashState.selected);
          if (prevFeature) return { ...prev, selected: prevFeature };
          return prev;
        });
        return true;
      } else if (event.code === ArrowDown) {
        setSlashState((prev) => {
          const nextFeature = getNextFeature(slashState.features, slashState.selected);
          if (nextFeature) return { ...prev, selected: nextFeature };
          return prev;
        });
        return true;
      } else if (event.code === Enter) {
        handleSelectedSlashFeature({ key: slashState.selected });
        return true;
      }
    }

    if (mentionState.open) {
      if (event.code === ArrowUp) {
        setMentionState((prev) => {
          const prevFeature = getPreviousFeature(mentionState.items, mentionState.selected);
          if (prevFeature) return { ...prev, selected: prevFeature };
          return prev;
        });
        return true;
      } else if (event.code === ArrowDown) {
        setMentionState((prev) => {
          const nextFeature = getNextFeature(mentionState.items, mentionState.selected);
          if (nextFeature) return { ...prev, selected: nextFeature };
          return prev;
        });
        return true;
      } else if (event.code === Enter) {
        const selectedMention = getSelectedFeature(mentionState.items, mentionState.selected);
        handleSelectedMentionFeature({ key: selectedMention?.key });
        setMentionState({ open: false, items: [], selected: '', range: { from: 0, to: 0 } });
        return true;
      }
    }
    return false;
  });

  const onDialogClose = useCallback(() => {
    setOpenDialog(null);
  }, []);

  const handleSelectedMentionFeature = (option) => {
    if (!mentionChange) return false;
    const { range } = mentionChange;
    const { key } = option;

    const stateMention = mentionState.items.mentions.find((mention) => mention?.key === key);
    const stateDocument = mentionState.items.documents.find((document) => document?.key === key);
    const stateWorkspace = mentionState.items.workspaces.find((workspace) => workspace?.key === key);

    chained.delete({ from: range.from, to: range.to });

    if (stateMention) {
      chained.insertMention({ id: key, name: stateMention?.displayName });
    } else if (stateDocument) {
      chained.insertText(stateDocument.displayName, range.from).updateLink(
        {
          href: stateDocument.document_info.url,
        },
        {
          from: range.from,
          to: range.from + stateDocument.displayName.length,
        }
      );
    } else if (stateWorkspace) {
      chained.insertText(stateWorkspace.displayName, range.from).updateLink(
        {
          href: stateWorkspace.workspace_info.url,
        },
        {
          from: range.from,
          to: range.from + stateWorkspace.displayName.length,
        }
      );
    }
    chained.focus().run();
  };

  const handleSelectedSlashFeature = (option) => {
    if (!slashState.open) return false;
    const {
      range: { from, to },
    } = slashState;

    if (!isContextMenu) {
      chained
        .customDispatch(({ tr, dispatch }) => {
          tr.delete(from, to);
          if (dispatch) dispatch(tr);
          return true;
        })
        .focus()
        .run();
    }

    const key = option?.key;
    switch (key) {
      case 'headings_1':
      case 'headings_2':
      case 'headings_3':
        const level = Number(key.split('_')[1]);
        if (!active.heading({ level })) {
          const id = `${Date.now()}-${Math.random().toString(36).substring(2, 8)}`;
          chained.toggleHeading({ id, level });
        } else {
          chained.convertParagraph();
        }
        break;
      case 'bulletList':
        chained.toggleBulletList();
        break;

      case 'orderedList':
        chained.toggleOrderedList();
        break;

      case 'taskList':
        chained.toggleTaskList().toggleCheckboxChecked({ checked: false });
        break;

      case 'table':
        chained.createTable({ rowsCount: 3, columnsCount: 3, withHeaderRow: true, totalWidth: width - 80 });
        break;

      case 'codeblock':
        chained.toggleCodeBlock();
        break;

      case 'blockquote':
        chained.toggleBlockquote();
        break;

      case 'horizontalRule':
        chained.insertHorizontalRule();
        break;

      case 'image':
        setOpenDialog('image');
        break;

      case 'video':
        setOpenDialog('video');
        break;

      case 'emoji':
        chained.insertText(':').focus().run();
        return;

      case 'date':
        setShowDatePicker({ flag: true, from });
        break;

      case 'copy': {
        const { state } = view;
        const text = state.selection.content().content.textBetween(0, state.selection.content().size, '\n');
        navigator.clipboard.writeText(text);
        setIsContextMenu(false);
        break;
      }
      case 'delete':
        chained.delete({ from, to });
        setIsContextMenu(false);
        break;
      case 'cut': {
        const { state } = view;
        const selectedContent = state.selection.content().content;
        const text = selectedContent.textBetween(0, selectedContent.size, '\n');
        navigator.clipboard.writeText(text).then(() => {
          chained.delete({ from, to }).run();
        });
        setIsContextMenu(false);
        break;
      }

      case 'info_calloutPanel':
      case 'warning_calloutPanel':
      case 'danger_calloutPanel':
      case 'success_calloutPanel':
        const type = key.split('_')[0];
        chained.toggleCallout({ type });
        break;

      case 'accordion':
        chained.insertAccordion();
        break;
    }
    chained.focus().run();
    setSlashState({ open: false, features, selected: getFirstFeature(features), range: { from: 0, to: 0 } });
  };

  const handleSelectedEmojiFeature = (option) => {
    if (!emojiChange || !option) return;
    const { range } = emojiChange;
    chained.delete({ from: range.from, to: range.to }).focus().run();
    const emoji = option?.emoji || '';
    chained
      .insertText(emoji + ' ')
      .focus()
      .run();
  };

  return (
    <>
      {!disableRichFeatures && (
        <>
          <RemirrorShortcut
            {...{
              editable,
              openDialog,
              onDialogClose,
              fileId,
              workspaceId,
              organizationId,
              items: slashState.features,
              open: slashState.open,
              selectedItem: slashState.selected,
              onClickHandler: handleSelectedSlashFeature,
              range: slashState.range,
            }}
          />
          <EmojiShortcut
            {...{
              editable,
              items: emojiState.items,
              open: emojiState.open,
              selectedItem: emojiState.selected,
              range: emojiState.range,
              onClickHandler: handleSelectedEmojiFeature,
            }}
          />
          <div style={{ position: 'relative' }}>
            <DatePickerShortcut open={showDatePicker} triggerToggleDatePicker={setShowDatePicker} />
          </div>
        </>
      )}
      <MentionShortcut
        {...{
          open: mentionState.open,
          items: mentionState.items,
          selectedItem: mentionState.selected,
          range: mentionState.range,
          onClickHandler: handleSelectedMentionFeature,
        }}
      />
    </>
  );
};

const getSelectedFeature = (groupedFeatures, currentFeatureKey, transformer = (obj) => obj) => {
  const allFeatures = Object.values(groupedFeatures).flat();
  const currentIndex = allFeatures.findIndex((feature) => feature.key === currentFeatureKey);
  if (currentIndex !== -1 && currentIndex <= allFeatures.length - 1) {
    return transformer(allFeatures[currentIndex]);
  }
  return null;
};

const getFirstFeature = (groupedFeatures) => {
  for (const group of Object.values(groupedFeatures)) {
    if (group.length > 0) {
      return group[0]?.key;
    }
  }
  return null;
};

const getNextFeature = (groupedFeatures, currentFeatureKey) => {
  const allFeatures = Object.values(groupedFeatures).flat();
  const currentIndex = allFeatures.findIndex((feature) => feature.key === currentFeatureKey);

  if (currentIndex !== -1 && currentIndex < allFeatures.length - 1) {
    return allFeatures[currentIndex + 1]?.key;
  }
  return null;
};

const getPreviousFeature = (groupedFeatures, currentFeatureKey) => {
  const allFeatures = Object.values(groupedFeatures).flat();
  const currentIndex = allFeatures.findIndex((feature) => feature.key === currentFeatureKey);

  if (currentIndex > 0) {
    return allFeatures[currentIndex - 1]?.key;
  }
  return null; // No previous feature available
};

export default ShortcutHOC;
